import React, { useEffect, useMemo, useState } from 'react';
import kkpService from '../../services/kkpService';
import { confirmVenteClear } from '../../utils/confirmVenteDelete';
import useAppConfirm from '../../hooks/useAppConfirm';
import { filterStatutsForMonth } from '../../utils/kkpStatutUtils';
import { briefCreatifToPayload, venteBriefFromApi } from '../../utils/kkpVenteBriefUtils';
import { briefPiecesJointesFromVente } from '../../utils/kkpBriefFichierUtils';
import { useKkpVenteBriefWizard } from '../../hooks/useKkpVenteBriefWizard';
import KkpClientAutocomplete from './KkpClientAutocomplete';
import KkpVenteWizardSteps from './KkpVenteWizardSteps';
import KkpVenteBriefCreativeStep from './KkpVenteBriefCreativeStep';
import KkpVenteWizardFooter from './KkpVenteWizardFooter';
import {
  AppFormField,
  AppInput,
  AppModal,
  AppSelect,
  AppTextarea,
} from '../ui';
import {
  batValideOptionTitle,
  interceptGraphisteBatValideRequest,
  interceptLockedBatValideChange,
  isBatValideOptionLocked,
} from '../../utils/kkpBatValide';
import '../../styles/kkp-vente-wizard.scss';

const EMPTY_FORM = {
  annee: new Date().getFullYear(),
  mois: new Date().getMonth() + 1,
  commercial_id: '',
  client_label: '',
  entreprise_locale_id: '',
  dolibarr_thirdparty_id: '',
  format: 'CARRE 1 G',
  statut_phase: 'Option',
  commentaire: '',
  brief_creatif: '',
};

const KkpVenteSetModal = ({
  show,
  onClose,
  onSaved,
  onDeleted,
  onError,
  meta,
  commerciaux = [],
  defaultCommercialId = '',
  initialValues = {},
  initialWizardStep,
  vente = null,
  onRequestBatValide,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { ask, modal } = useAppConfirm();
  const {
    currentStep,
    setCurrentStep,
    savedVente,
    setSavedVente,
    piecesJointes,
    setPiecesJointes,
    activeVente,
    activeVenteId,
    shouldCloseAfterCreate,
  } = useKkpVenteBriefWizard({ show, vente, initialWizardStep, initialValues });

  const statutOptions = useMemo(
    () => filterStatutsForMonth(meta?.statuts, form.annee, form.mois),
    [meta?.statuts, form.annee, form.mois],
  );

  useEffect(() => {
    if (!show) return;

    if (vente) {
      setForm({
        annee: vente.annee,
        mois: vente.mois,
        commercial_id: vente.commercial_id ?? '',
        client_label: vente.client_label,
        entreprise_locale_id: vente.entreprise_locale_id ?? '',
        dolibarr_thirdparty_id: vente.dolibarr_thirdparty_id ?? '',
        format: vente.format,
        statut_phase: vente.statut_phase,
        commentaire: vente.commentaire ?? '',
        brief_creatif: venteBriefFromApi(vente.brief_encart),
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        annee: initialValues.annee ?? new Date().getFullYear(),
        mois: initialValues.mois ?? new Date().getMonth() + 1,
        commercial_id: initialValues.commercial_id ?? defaultCommercialId ?? '',
        format: initialValues.format ?? meta?.formats?.[0]?.value ?? 'CARRE 1 G',
        statut_phase: initialValues.statut_phase ?? filterStatutsForMonth(meta?.statuts, initialValues.annee ?? new Date().getFullYear(), initialValues.mois ?? new Date().getMonth() + 1)[0]?.value ?? 'Option',
        ...initialValues,
        commercial_id: defaultCommercialId || '',
        brief_creatif: venteBriefFromApi(initialValues.brief_encart),
      });
    }
  }, [show, vente, initialValues, defaultCommercialId, meta]);

  useEffect(() => {
    if (!show || statutOptions.length === 0) return;
    if (!statutOptions.some((s) => s.value === form.statut_phase)) {
      setForm((f) => ({ ...f, statut_phase: statutOptions[0].value }));
    }
  }, [show, form.annee, form.mois, statutOptions]);

  const buildPayload = () => ({
    ...form,
    commercial_id: form.commercial_id || defaultCommercialId || null,
    entreprise_locale_id: form.entreprise_locale_id || null,
    dolibarr_thirdparty_id: form.dolibarr_thirdparty_id || null,
    brief_encart: briefCreatifToPayload(form.brief_creatif),
  });

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = buildPayload();
      if (activeVenteId) {
        const updated = await kkpService.updateVenteSet(activeVenteId, payload);
        setSavedVente(updated);
        setPiecesJointes(briefPiecesJointesFromVente(updated));
        onSaved?.();
        onClose();
      } else {
        const created = await kkpService.createVenteSet(payload);
        setSavedVente(created);
        setPiecesJointes(briefPiecesJointesFromVente(created));
        onSaved?.();
        if (shouldCloseAfterCreate(currentStep)) {
          onClose();
        }
      }
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!activeVenteId) return;
    if (!(await confirmVenteClear(ask, form.client_label))) return;

    setDeleting(true);
    try {
      await kkpService.clearVenteSet(activeVenteId);
      onDeleted?.();
      onClose();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
    <AppModal
      show={show}
      title={activeVenteId ? 'Modifier la vente' : 'Nouvelle vente SET'}
      onClose={onClose}
      size="lg"
      centered
      wrapBody={false}
      contentAs="form"
      contentProps={{ onSubmit: handleSave }}
    >

            <KkpVenteWizardSteps currentStep={currentStep} onStepChange={setCurrentStep} />

            <div className="kkp-vente-wizard-body">
              {currentStep === 0 && (
                <div className="row g-3">
                  <AppFormField className="col-md-6" label="Année" htmlFor="kkp-set-annee" required>
                    <AppInput
                      id="kkp-set-annee"
                      type="number"
                      required
                      value={form.annee}
                      onChange={(e) => setForm((f) => ({ ...f, annee: Number(e.target.value) }))}
                    />
                  </AppFormField>
                  <AppFormField className="col-md-6" label="Mois" htmlFor="kkp-set-mois" required>
                    <AppSelect
                      id="kkp-set-mois"
                      required
                      value={form.mois}
                      onChange={(e) => setForm((f) => ({ ...f, mois: Number(e.target.value) }))}
                    >
                      {(meta?.mois ?? []).map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                  <AppFormField className="col-12" label="Client" required>
                    <KkpClientAutocomplete
                      value={form.client_label}
                      onChange={(val) => setForm((f) => ({
                        ...f,
                        client_label: val,
                        entreprise_locale_id: '',
                        dolibarr_thirdparty_id: '',
                      }))}
                      onSelect={(client) => setForm((f) => ({
                        ...f,
                        client_label: client.nom ?? '',
                        dolibarr_thirdparty_id: Number(client?.id) > 0 ? Number(client.id) : '',
                        entreprise_locale_id: '',
                      }))}
                      required
                    />
                  </AppFormField>
                  <AppFormField className="col-md-6" label="Format" htmlFor="kkp-set-format" required>
                    <AppSelect
                      id="kkp-set-format"
                      required
                      value={form.format}
                      onChange={(e) => setForm((f) => ({ ...f, format: e.target.value }))}
                    >
                      {(meta?.formats ?? []).map((f) => (
                        <option key={f.value} value={f.value}>{f.value}</option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                  <AppFormField className="col-md-6" label="Statut phase" htmlFor="kkp-set-statut" required>
                    <AppSelect
                      id="kkp-set-statut"
                      required
                      value={form.statut_phase}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (interceptLockedBatValideChange(next, form.statut_phase)) return;
                        if (interceptGraphisteBatValideRequest(next, form.statut_phase, activeVente, onRequestBatValide)) {
                          return;
                        }
                        setForm((f) => ({ ...f, statut_phase: next }));
                      }}
                    >
                      {statutOptions.map((s) => (
                        <option
                          key={s.value}
                          value={s.value}
                          title={batValideOptionTitle(s.value)}
                          style={isBatValideOptionLocked(s.value) ? { color: '#9ca3af' } : undefined}
                        >
                          {s.value}
                        </option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                  <AppFormField className="col-12" label="Commentaire" htmlFor="kkp-set-commentaire">
                    <AppTextarea
                      id="kkp-set-commentaire"
                      rows={2}
                      placeholder="Notes internes (hors brief créatif)"
                      value={form.commentaire}
                      onChange={(e) => setForm((f) => ({ ...f, commentaire: e.target.value }))}
                    />
                  </AppFormField>
                </div>
              )}

              {currentStep === 1 && (
                <KkpVenteBriefCreativeStep
                  value={form.brief_creatif}
                  onChange={(value) => setForm((f) => ({ ...f, brief_creatif: value }))}
                  venteId={activeVenteId}
                  piecesJointes={piecesJointes}
                  onPiecesJointesChange={setPiecesJointes}
                  onError={onError}
                />
              )}
            </div>

            <KkpVenteWizardFooter
              currentStep={currentStep}
              onStepChange={setCurrentStep}
              onClose={onClose}
              onSave={handleSave}
              onDelete={handleClear}
              saving={saving}
              deleting={deleting}
              showDelete={Boolean(activeVenteId)}
              deleteLabel="Effacer"
              deletingLabel="Effacement…"
              deleteVariant="outline-warning"
              showCompleteLater={currentStep === 1}
            />
    </AppModal>
    {modal}
    </>
  );
};

export default KkpVenteSetModal;
